import React, { useEffect, useRef, useState } from "react";
import Icon from "../../components/Icon";
import Button from "../../components/Button";

const A4_RATIO = 210 / 297;

// Mesmos limites do backend: o arquivo é embutido em cada PDF gerado, que fica
// guardado e vai anexado no e-mail.
export const COVER = {
    noun: 'capa',
    extensions: ['pdf', 'jpg', 'jpeg', 'png'],
    accept: '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png',
    maxSize: 2 * 1024 * 1024,
    maxSizeLabel: '2 MB',
    formatError: 'Formato não aceito. Envie um PDF ou uma imagem JPG/PNG.',
    hint: 'PDF ou imagem em A4 retrato, até 2 MB',
    // A imagem é esticada para a página inteira no backend.
    a4: true,
};

// Assinatura/carimbo do final do PDF (BDM-1253): só imagem, reduzida no PDF.
export const SIGNATURE = {
    noun: 'assinatura',
    extensions: ['jpg', 'jpeg', 'png'],
    accept: '.jpg,.jpeg,.png,image/jpeg,image/png',
    maxSize: 1024 * 1024,
    maxSizeLabel: '1 MB',
    formatError: 'Formato não aceito. Envie uma imagem PNG ou JPG.',
    hint: 'PNG com fundo transparente ou JPG, até 1 MB',
    a4: false,
};

const capitalize = text => text.charAt(0).toUpperCase() + text.slice(1);

const extensionOf = name => (name ?? '').split('.').pop().toLowerCase();

const typeLabel = extension => extension === 'pdf' ? 'PDF' : `Imagem ${extension === 'png' ? 'PNG' : 'JPG'}`;

const formatSize = bytes => bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;

const validate = (file, config) => {
    if (!config.extensions.includes(extensionOf(file.name))) {
        return config.formatError;
    }

    if (file.size > config.maxSize) {
        return `O arquivo tem ${formatSize(file.size)} e a ${config.noun} deve ter até ${config.maxSizeLabel}.`;
    }

    return null;
}

const Thumb = ({ extension, src }) => (
    <div className="cover-field-thumb">
        {extension !== 'pdf' && src
            ? <img src={src} alt="" />
            : <span className="cover-field-thumb-pdf"><Icon name="pdf" size={18} /></span>
        }
    </div>
);

export const CoverField = ({ config = COVER, savedUrl, savedName, removed, file, onSelect, onCancel, onRemove, onUndoRemove }) => {
    const input = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);
    // Na capa, a imagem é esticada para a página inteira no backend: fora do A4
    // retrato ela sai deformada, então o aviso vem antes de salvar.
    const [outOfA4, setOutOfA4] = useState(false);

    const fileExtension = file ? extensionOf(file.name) : null;

    useEffect(() => {
        setOutOfA4(false);

        if (!file || fileExtension === 'pdf') {
            setPreview(null);
            return;
        }

        const url = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => setOutOfA4(config.a4 && Math.abs(image.naturalWidth / image.naturalHeight - A4_RATIO) > 0.02);
        image.src = url;
        setPreview(url);

        return () => URL.revokeObjectURL(url);
    }, [file, fileExtension, config.a4]);

    const choose = () => input.current?.click();

    const receive = selected => {
        if (!selected) return;

        const message = validate(selected, config);

        setError(message);

        if (!message) onSelect(selected);
    }

    const dropHandlers = {
        onDragOver: event => { event.preventDefault(); setDragging(true); },
        onDragLeave: event => { if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); },
        onDrop: event => {
            event.preventDefault();
            setDragging(false);
            receive(event.dataTransfer.files?.[0]);
        }
    };

    const hasSaved = !!savedUrl && !removed;
    const savedExtension = extensionOf(savedName);

    return (
        <div className={`cover-field${config.a4 ? '' : ' is-free'}`}>
            <input
                ref={input}
                type="file"
                accept={config.accept}
                hidden
                onChange={event => { receive(event.target.files?.[0]); event.target.value = ''; }}
            />

            {file &&
                <div className={`cover-field-card${dragging ? ' is-dragging' : ''}`} {...dropHandlers}>
                    <Thumb extension={fileExtension} src={preview} />
                    <div className="cover-field-info">
                        <strong title={file.name}>{file.name}</strong>
                        <span>{typeLabel(fileExtension)} · {formatSize(file.size)} · salve para aplicar</span>
                    </div>
                    <div className="cover-field-actions">
                        <Button type="secondary" size="small" text="Trocar" action={choose} />
                        <button type="button" className="cover-field-icon-button" onClick={onCancel} title="Descartar arquivo" aria-label="Descartar arquivo">
                            <Icon name="close" size={16} />
                        </button>
                    </div>
                </div>
            }

            {!file && hasSaved &&
                <div className={`cover-field-card${dragging ? ' is-dragging' : ''}`} {...dropHandlers}>
                    <Thumb extension={savedExtension} src={savedUrl} />
                    <div className="cover-field-info">
                        <strong>{capitalize(config.noun)} atual</strong>
                        <span className="cover-field-status">
                            <Icon name="task-completed" size={14} />
                            <em>Em uso</em> · {typeLabel(savedExtension)}
                        </span>
                    </div>
                    <div className="cover-field-actions">
                        <a className="cover-field-icon-button" href={savedUrl} target="_blank" rel="noopener noreferrer" title={`Visualizar ${config.noun}`} aria-label={`Visualizar ${config.noun}`}>
                            <Icon name="eye" size={16} />
                        </a>
                        <Button type="secondary" size="small" text="Trocar" action={choose} />
                        <button type="button" className="cover-field-icon-button danger" onClick={onRemove} title={`Remover ${config.noun}`} aria-label={`Remover ${config.noun}`}>
                            <Icon name="delete-small" size={16} />
                        </button>
                    </div>
                </div>
            }

            {!file && !hasSaved &&
                <button
                    type="button"
                    className={`cover-field-dropzone${dragging ? ' is-dragging' : ''}`}
                    onClick={choose}
                    {...dropHandlers}
                >
                    <Icon name="upload-small" size={18} />
                    <span className="cover-field-info">
                        <strong><u>Escolha um arquivo</u> ou arraste aqui</strong>
                        <span>{config.hint}</span>
                    </span>
                </button>
            }

            {!file && removed && savedUrl &&
                <p className="cover-field-message">
                    A {config.noun} atual será removida ao salvar. <button type="button" onClick={onUndoRemove}>Desfazer</button>
                </p>
            }
            {error &&
                <p className="cover-field-message error" role="alert">{error}</p>
            }
            {!error && outOfA4 &&
                <p className="cover-field-message warning">A imagem não está em A4 retrato e vai sair esticada na página.</p>
            }
        </div>
    );
}

export default CoverField;
